/**
 * v2.78 Phase A — Property normalisation helpers (frontend mirror of
 * backend `utils.normalize_property_type` / `normalize_bhk` / `derive_source_label`).
 *
 * READ-TIME only. We do NOT mutate stored DB records — these helpers are
 * used to:
 *   • normalise filter chip values BEFORE sending to /properties/search
 *   • normalise display values in PropertyCard / PropertyDetail
 *   • drive the canonical dropdown in UnifiedSubmissionForm
 *
 * Single source of truth lives in this file. Any future alias additions
 * must mirror /app/backend/utils.py PROPERTY_TYPE_ALIASES.
 */

// ─────────────────────────── Property type ───────────────────────────

/** Canonical → list of aliases that may appear in legacy DB docs.
 *  v2.88 — Final residential policy: "Apartment" and "Flat" REMOVED as
 *  standalone canonical types. Both (plus "Flat/Apartment", "Society Flat",
 *  "Group Housing", "DDA/CGHS Flat" etc.) now normalize to "Builder Floor".
 *  "Full Building" renamed to "Building" as the canonical name.
 *  Data migration (backend/migrations/v288_flat_apartment_to_builder_floor.py)
 *  has already normalized existing docs — this map stays populated so any
 *  legacy API request, saved search, or bulk-import row with the old values
 *  is still correctly resolved at read time. KEEP IN SYNC with
 *  `backend/utils.py` PROPERTY_TYPE_ALIASES. */
export const PROPERTY_TYPE_ALIASES = {
  "Builder Floor":     ["Builder Floor", "builder floor", "BuilderFloor", "builder_floor",
                        "Floor", "floor", "Independent Floor", "Builder Floors",
                        "Flat", "flat", "FLAT",
                        "Apartment", "apartment", "APARTMENT",
                        "Flat/Apartment", "flat/apartment",
                        "Society Flat", "Society Apartment", "Group Housing",
                        "DDA Flat", "CGHS Flat", "CGHS", "DDA"],
  "Independent House": ["Independent House", "House", "house", "HOUSE",
                        "Villa", "villa", "Bungalow", "Kothi"],
  "Plot":              ["Plot", "plot", "PLOT", "Land", "land", "Plot/Land"],
  "Shop":              ["Shop", "shop", "SHOP", "Retail", "Showroom", "Commercial Shop"],
  "Office":            ["Office", "office", "OFFICE", "Commercial Office",
                        "Workspace", "Commercial Space"],
  "Building":          ["Building", "building", "BUILDING",
                        "Full Building", "Full building", "full building",
                        "Whole Building", "Entire Building"],
};

/** Single source of truth for the property-type dropdown. */
export const PROPERTY_TYPE_CANONICAL = Object.keys(PROPERTY_TYPE_ALIASES);

// Reverse-lookup: alias (lowercased) → canonical key.
const _PT_REVERSE = {};
for (const [canonical, aliases] of Object.entries(PROPERTY_TYPE_ALIASES)) {
  for (const a of aliases) _PT_REVERSE[a.toLowerCase().trim()] = canonical;
}

/**
 * Return the canonical Title-Case property type for any known alias.
 * Unknown strings are returned as-is (stripped).
 */
export function normalizePropertyType(raw) {
  if (!raw) return "";
  const key = String(raw).trim().toLowerCase();
  if (_PT_REVERSE[key]) return _PT_REVERSE[key];
  return String(raw).trim();
}

// ─────────────────────────── BHK ───────────────────────────

export const BHK_CANONICAL = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK"];

/**
 * Canonicalise BHK values like '2', '2BHK', '2 bhk', ' 2  BHK ' → '2 BHK'.
 * Non-numeric sentinels ('Not Specified', 'Plot', 'Shop') are returned
 * stripped, unchanged.
 */
export function normalizeBhk(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const m = s.match(/^\s*(\d{1,2})\s*(bhk)?\s*$/i);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 20) return `${n} BHK`;
  }
  return s;
}

// ─────────────────────────── Source label ───────────────────────────

const SOURCE_LABEL_BY_ROLE = {
  dealer:      "Dealer",
  builder:     "Builder",
  owner:       "Owner",
  admin:       "Admin",
  team:        "Admin",
  team_member: "Admin",
};

/**
 * Return a display label for who listed the property.
 *
 * Priority:
 *   1. Explicit non-empty `source` (Dealer/Builder/Owner override set by admin)
 *      — UNLESS source === "Dealer" (the schema default), in which case we
 *      still prefer the role mapping so admin-created docs read as "Admin".
 *   2. Mapping of `submitted_by_role` → display label
 *   3. Fallback: "Contact"
 */
export function deriveSourceLabel(submitted_by_role, source) {
  const src = (source || "").trim();
  if (src && src.toLowerCase() !== "dealer") {
    return src[0].toUpperCase() + src.slice(1);
  }
  const role = (submitted_by_role || "").trim().toLowerCase();
  if (SOURCE_LABEL_BY_ROLE[role]) return SOURCE_LABEL_BY_ROLE[role];
  if (src) return src[0].toUpperCase() + src.slice(1);
  return "Contact";
}

/**
 * v2.85 Phase A — Public "Posted By" label for property detail / cards.
 * Same priority chain as deriveSourceLabel but with a user-friendly
 * fallback ("Listed By") instead of the admin-internal "Contact".
 * Admin role is also mapped to "Listed By" publicly so anonymous
 * viewers don't see internal role distinctions.
 *
 * Returns one of: "Owner" | "Dealer" | "Builder" | "Listed By".
 */
export function derivePublicSourceLabel(property) {
  const lbl = deriveSourceLabel(property?.submitted_by_role, property?.source);
  if (!lbl || lbl === "Contact" || lbl === "Admin") return "Listed By";
  return lbl;
}

// ─────────────────────────── Tag de-duplication ───────────────────────────

/**
 * v2.78 Phase A — Build the visible tag list for a property.
 *
 * Rule: boolean curation flags (best_deal / premium_deal / featured) are
 * the source of truth. If the stored `tags` array ALSO contains the same
 * label, we drop the duplicate so a single listing shows the chip just
 * once.
 *
 * Returns: array of display labels (Title Case strings).
 */
const CURATION_TAG_NAMES = new Set([
  "best deal",
  "premium deal",
  "featured",
]);

export function buildDisplayTags(property) {
  const out = [];
  if (property.best_deal) out.push("Best Deal");
  if (property.premium_deal) out.push("Premium Deal");
  if (property.featured) out.push("Featured");
  for (const t of (property.tags || [])) {
    const tk = String(t || "").trim();
    if (!tk) continue;
    if (CURATION_TAG_NAMES.has(tk.toLowerCase())) continue; // covered by boolean
    out.push(tk);
  }
  // Preserve insertion order, de-dup case-insensitively for safety
  const seen = new Set();
  const dedup = [];
  for (const t of out) {
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    dedup.push(t);
  }
  return dedup;
}
